import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export const FILE_SYNC_MODULE = {
  PROJECT: "project",
  SPACE: "space",
  HIERARCHY_ID: "hierarchyId",
} as const;

export type FileSyncModuleName = (typeof FILE_SYNC_MODULE)[keyof typeof FILE_SYNC_MODULE];

/** A single file sync state entry (from Electron API) */
export interface FileSyncStateEntry {
  spaceId?: string;
  projectId?: string;
  hierarchyId?: string;
  status?: string;
  progress?: number;
  name?: string;
  [key: string]: string | number | boolean | null | undefined;
}

/** The file sync data payload from Electron API */
export interface FileSyncData {
  state?: FileSyncStateEntry[];
  [key: string]: FileSyncStateEntry[] | string | number | boolean | null | undefined;
}

/** Entry stored per module+id in the mapper */
export interface FileSyncMapperEntry {
  isLoading: boolean;
  isLoaded: boolean;
  data: FileSyncMapperEntryData | null;
}

/** Data for a file sync mapper entry (project, space, or hierarchyId details) */
export interface FileSyncMapperEntryData {
  filterPath?: string;
  projectId?: string;
  name?: string;
  spaceId?: string;
  id?: string;
  _id?: string;
  [key: string]: string | number | boolean | null | undefined;
}

type FileSyncModuleMap = Record<string, FileSyncMapperEntry>;

interface FileSyncMapperState {
  [FILE_SYNC_MODULE.PROJECT]: FileSyncModuleMap;
  [FILE_SYNC_MODULE.SPACE]: FileSyncModuleMap;
  [FILE_SYNC_MODULE.HIERARCHY_ID]: FileSyncModuleMap;
}

interface FileSyncState {
  minimiseState: string;
  data: FileSyncData;
}

const initialState: FileSyncState = {
  minimiseState: "CLOSED_FILE_SYNC",
  data: {},
};

interface FileSyncMapperPayload {
  moduleName: FileSyncModuleName;
  moduleId: string;
  data?: FileSyncMapperEntryData;
}

const fileSyncSlice = createSlice({
  name: "fileSyncState",
  initialState,
  reducers: {
    minimiseFileSync: (state: FileSyncState) => {
      state.minimiseState = "MINIMISED_FILE_SYNC";
    },
    maximiseFileSync: (state: FileSyncState) => {
      state.minimiseState = "MAXIMISED_FILE_SYNC";
    },
    closeFileSync: (state: FileSyncState) => {
      state.minimiseState = "CLOSED_FILE_SYNC";
    },
    setFileSyncData: (state: FileSyncState, action: PayloadAction<FileSyncData>) => {
      if (state.minimiseState === "CLOSED_FILE_SYNC") {
        state.minimiseState = "MINIMISED_FILE_SYNC";
      }
      state.data = action.payload;
    },
  },
});

const fileSyncMapperSlice = createSlice({
  name: "fileSyncMapper",
  initialState: {
    [FILE_SYNC_MODULE.PROJECT]: {},
    [FILE_SYNC_MODULE.SPACE]: {},
    [FILE_SYNC_MODULE.HIERARCHY_ID]: {},
  } as FileSyncMapperState,
  reducers: {
    fileSyncMapperLoading: (state: FileSyncMapperState, action: PayloadAction<FileSyncMapperPayload>) => {
      state[action.payload.moduleName][action.payload.moduleId] = {
        isLoading: true,
        isLoaded: false,
        data: null,
      };
    },
    fileSyncMapperLoaded: (state: FileSyncMapperState, action: PayloadAction<FileSyncMapperPayload>) => {
      state[action.payload.moduleName][action.payload.moduleId] = {
        isLoading: false,
        isLoaded: true,
        data: action.payload.data ?? null,
      };
    },
    fileSyncMapperClear: (state: FileSyncMapperState, action: PayloadAction<FileSyncMapperPayload>) => {
      state[action.payload.moduleName][action.payload.moduleId] = {
        isLoading: false,
        isLoaded: false,
        data: null,
      };
    },
  },
});

export const { minimiseFileSync, maximiseFileSync, closeFileSync, setFileSyncData } = fileSyncSlice.actions;

export const { fileSyncMapperLoading, fileSyncMapperLoaded, fileSyncMapperClear } = fileSyncMapperSlice.actions;

export const fileSyncState = fileSyncSlice.reducer;
export const fileSyncMapperState = fileSyncMapperSlice.reducer;
